import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ErrorState,
  PageHeader,
  Skeleton,
} from '@sitewise/ui';
import { useQuery } from '@tanstack/react-query';

import { fetchAdminAiUsage } from '../../api/ai.js';

/**
 * `/admin/ai` — global AI usage dashboard. Shows the last 30 days of
 * spend, broken down by feature and status. The per-org drill-in lives
 * on the org-detail page (Phase 8.future), but the global view is enough
 * to spot runaway features.
 */
export function AdminAiUsagePage() {
  const usageQuery = useQuery({
    queryKey: ['admin-ai-usage'],
    queryFn: fetchAdminAiUsage,
  });

  return (
    <>
      <PageHeader
        title="AI usage"
        description="Calls and spend across all organizations for the trailing 30 days."
      />

      {usageQuery.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : usageQuery.isError ? (
        <ErrorState description="Could not load AI usage." />
      ) : !usageQuery.data ? null : (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Breakdown by feature</CardTitle>
            <p className="text-xs text-fg-muted">
              Since {new Date(usageQuery.data.sinceUtc).toLocaleString()}
            </p>
          </CardHeader>
          <CardContent>
            {usageQuery.data.breakdown.length === 0 ? (
              <p className="text-sm text-fg-muted">No AI calls in the last 30 days.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wide text-fg-muted">
                  <tr>
                    <th className="pb-2 font-medium">Feature</th>
                    <th className="pb-2 font-medium">Status</th>
                    <th className="pb-2 text-right font-medium">Calls</th>
                    <th className="pb-2 text-right font-medium">Spend (USD)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {usageQuery.data.breakdown.map((row, i) => (
                    <tr key={i}>
                      <td className="py-1.5">{row.feature}</td>
                      <td className="py-1.5">
                        <StatusPill status={row.status} />
                      </td>
                      <td className="py-1.5 text-right tabular-nums">
                        {row.calls.toLocaleString()}
                      </td>
                      <td className="py-1.5 text-right tabular-nums">
                        ${row.spendUsd.toFixed(4)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}
    </>
  );
}

function StatusPill({ status }: { status: string }) {
  const tone =
    status === 'ok'
      ? 'bg-success-subtle text-success'
      : status === 'blocked'
        ? 'bg-warning-subtle text-warning'
        : status === 'error'
          ? 'bg-danger-subtle text-danger'
          : 'bg-surface-muted text-fg-muted';
  return (
    <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${tone}`}>
      {status}
    </span>
  );
}
